//shared containers
import { Footer } from '@/containers-m/Footer';
import { Navigation } from '/containers-m/';

//containers
import {
  Hero,
  WatchItRunYourFreight,
  HowItWorks,
  RunsOnYourStack,
  TheRestOfTheLineup,
  EndCTA,
} from '../containers';

//styles
import {
  PageWrapper,
  PurchaseOrderFollowUpGlobalStyle,
} from './PurchaseOrderFollowUp.styles';

const hero = {
  slug: 'purchase-order-follow-up',
  title: 'Purchase Order Follow-Up',
  description:
    'Finds purchase orders that are due with no dock appointment, or with a booking that won’t arrive in time, and gets the supplier or carrier to ship, book or rebook before stock runs short.',
  metric: '40–50%',
  metricLabel: 'more on-time deliveries',
};

const watchItRun = {
  title: 'Nine POs are due with no appointment',
  stageHeadTitle: 'Nine POs due this week have no dock appointment.',
  stageHeadMeta: 'PO Follow-Up · Inbound DC-04',
  rows: [
    {
      time: '06:00:00',
      text: 'Checked 412 open POs due in the next 5 days against appointments and bookings.',
    },
    {
      time: '06:00:20',
      text: '9 POs with no appointment. 2 booked loads tracking past their slot.',
    },
    {
      time: '06:01:10',
      text: 'Emailed 7 suppliers to release the orders. Rebooked the 2 late loads.',
    },
    {
      time: '09:12:00',
      text: '5 suppliers confirmed. Their carriers booked slots for 7 of the 9 POs.',
    },
    {
      time: '09:15:00',
      text: 'All but 2 POs booked by 9:15. Those 2 are on the buyer’s at-risk list.',
    },
  ],
  summaryText:
    'Nine purchase orders were due with no truck booked, and two booked loads were running late. Loadsmart AI found them before the receiving window, got suppliers and carriers to book, rebooked the late loads, and gave the buyer a short list of the two still open.',
  agentBadge: 'PO Follow-Up Agent',
  escalationTitle: 'Unanswered POs go to the buyer.',
  escalationText:
    'The agent follows up with suppliers and carriers on its own. When one doesn’t respond, the PO goes on the buyer’s daily at-risk list with every follow-up logged.',
};

const howItWorks = {
  stepsTitleWhite: 'Matches every PO to a booking,',
  stepsTitleGradient: 'and chases the ones without one.',
  steps: [
    {
      title: 'Matches POs to bookings',
      description:
        'Compares open POs and ASNs in your ERP or WMS with dock appointments, vendor portals and TMS bookings.',
    },
    {
      title: 'Spots the loads that won’t make it',
      description:
        'Uses carrier tracking to find booked loads whose ETA lands after the slot or the due date.',
    },
    {
      title: 'Contacts the right party',
      description:
        'Asks the supplier to release the order, or the carrier to book, ship or rebook.',
    },
    {
      title: 'Keeps the buyer informed',
      description:
        'Sends a daily list of POs still at risk, with what was done on each.',
    },
  ],
  outcomes: [
    {
      title: 'Fewer surprises at receiving',
      description:
        'Missing deliveries are found days ahead, while there is still time to book them.',
    },
    {
      title: 'Every open PO gets followed up',
      description:
        'Suppliers and carriers hear from the agent every day, on every PO, including the small ones.',
    },
    {
      title: 'Stock arrives when it’s needed',
      description: 'Inbound freight lands on the date the PO calls for.',
    },
  ],
};

const lineupCards = [
  {
    slug: 'scheduling-and-re-scheduling',
    title: 'Scheduling & Re-Scheduling',
    description: 'Books the dock slot once the supplier confirms.',
  },
  {
    slug: 'tracking-and-status-updates',
    title: 'Tracking & Status Updates',
    description: 'Keeps the ETA current once the load is moving.',
  },
  {
    slug: 'document-collection-and-filing',
    title: 'Document Collection & Filing',
    description:
      'We capture every quote, tender, rate con, BOL, POD and invoice as it arrives, and file it against the right shipment automatically.',
  },
  {
    slug: 'tender-failures',
    title: 'Tender Failures',
    description:
      'When a tender falls through, we re-tender down your routing guide and re-book, before the miss ever reaches your team.',
  },
  {
    slug: 'proactive-load-audits',
    title: 'Proactive Load Audits',
    description:
      'We check every load before pickup and flag at-risk transit early, so problems surface before they turn into penalties.',
  },
  {
    slug: 'claims-processing',
    title: 'Claims Processing',
    description:
      'We gather the documentation, validate the claim, and draft the response for your approval, so claims get filed and recovered.',
  },
  {
    slug: 'detention-handling',
    title: 'Detention Handling',
    description:
      'We warn the dock before free time runs out, and approve detention requests that match the gate record.',
  },
  {
    slug: 'warehouse-customer-service',
    title: 'Warehouse Customer Service',
    description:
      'We answer your warehouse’s status questions and book appointments from an email, using your dock schedule and WMS.',
  },
];

function PurchaseOrderFollowUp() {
  return (
    <PageWrapper>
      <PurchaseOrderFollowUpGlobalStyle />
      <Navigation theme="dark" />
      <Hero {...hero} />
      <WatchItRunYourFreight {...watchItRun} />
      <HowItWorks {...howItWorks} />
      <RunsOnYourStack />
      <TheRestOfTheLineup cards={lineupCards} />
      <EndCTA agentName="Purchase Order Follow-Up" />
      <Footer />
    </PageWrapper>
  );
}

export default PurchaseOrderFollowUp;
export { PurchaseOrderFollowUp };
